/**
 * =============================================================================
 *  EmployeeCloneSlice.js
 * -----------------------------------------------------------------------------
 *  Redux Toolkit slice for the multi-profile patient clone / transfer workflow.
 * =============================================================================
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import EmployeeCloneService from '../services/EmployeeCloneService';

export const cloneEmployeeProfile = createAsyncThunk(
  'employeeClone/cloneEmployeeProfile',
  async ({ employeeId, payload }, { rejectWithValue }) => {
    try {
      const response = await EmployeeCloneService.cloneEmployee(employeeId, payload);
      return {
        employeeId,
        data: response?.data || null,
        message: response?.message || 'Profile cloned successfully.',
      };
    } catch (error) {
      return rejectWithValue(error?.message || 'Clone failed');
    }
  }
);

export const reassignEmployeeProfile = createAsyncThunk(
  'employeeClone/reassignEmployeeProfile',
  async ({ employeeId, payload }, { rejectWithValue }) => {
    try {
      const response = await EmployeeCloneService.reassignEmployee(employeeId, payload);
      return {
        employeeId,
        data: response?.data || null,
        message: response?.message || 'Patient transferred successfully.',
      };
    } catch (error) {
      return rejectWithValue(error?.message || 'Transfer failed');
    }
  }
);

export const detectDuplicateProfiles = createAsyncThunk(
  'employeeClone/detectDuplicateProfiles',
  async (demographics, { rejectWithValue }) => {
    try {
      const response = await EmployeeCloneService.duplicateCheck(demographics);
      return Array.isArray(response?.data) ? response.data : [];
    } catch (error) {
      return rejectWithValue(error?.message || 'Duplicate detection failed');
    }
  }
);

const initialState = {
  cloneInProgress: false,
  cloneError: null,
  lastCloneResult: null,
  duplicateHints: [],
  duplicateHintsLoading: false,
};

const EmployeeCloneSlice = createSlice({
  name: 'employeeClone',
  initialState,
  reducers: {
    clearLastCloneResult(state) {
      state.lastCloneResult = null;
      state.cloneError = null;
    },
    clearDuplicateHints(state) {
      state.duplicateHints = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(cloneEmployeeProfile.pending, (state) => {
        state.cloneInProgress = true;
        state.cloneError = null;
        state.lastCloneResult = null;
      })
      .addCase(cloneEmployeeProfile.fulfilled, (state, action) => {
        state.cloneInProgress = false;
        state.lastCloneResult = action.payload;
      })
      .addCase(cloneEmployeeProfile.rejected, (state, action) => {
        state.cloneInProgress = false;
        state.cloneError = action.payload || 'Clone failed';
      })
      .addCase(reassignEmployeeProfile.pending, (state) => {
        state.cloneInProgress = true;
        state.cloneError = null;
        state.lastCloneResult = null;
      })
      .addCase(reassignEmployeeProfile.fulfilled, (state, action) => {
        state.cloneInProgress = false;
        state.lastCloneResult = action.payload;
      })
      .addCase(reassignEmployeeProfile.rejected, (state, action) => {
        state.cloneInProgress = false;
        state.cloneError = action.payload || 'Transfer failed';
      })
      .addCase(detectDuplicateProfiles.pending, (state) => {
        state.duplicateHintsLoading = true;
      })
      .addCase(detectDuplicateProfiles.fulfilled, (state, action) => {
        state.duplicateHintsLoading = false;
        state.duplicateHints = action.payload || [];
      })
      .addCase(detectDuplicateProfiles.rejected, (state) => {
        state.duplicateHintsLoading = false;
      });
  },
});

export const { clearLastCloneResult, clearDuplicateHints } = EmployeeCloneSlice.actions;

export const selectCloneInProgress = (state) =>
  state.employeeClone?.cloneInProgress || false;

export const selectLastCloneResult = (state) =>
  state.employeeClone?.lastCloneResult || null;

export const selectDuplicateHints = (state) =>
  state.employeeClone?.duplicateHints || [];

export default EmployeeCloneSlice.reducer;
